<template>
  <modal
    :loading="loading"
    :title="$t('set_labels.import_title')"
    style="max-width: 720px"
    @close_modal="close_modal"
  >
    <template v-slot:content>
      <p class="import-help">{{ $t('set_labels.import_help') }}</p>
      <p class="import-format">
        <code>name</code>
        {{ $t('set_labels.import_format_or') }}
        <code>name,color,type,parent</code>
      </p>
      <p class="import-defaults">{{ $t('set_labels.import_defaults') }}</p>

      <v-file-input
        id="import_labels_file"
        v-model="file"
        accept=".csv,text/csv"
        :label="$t('set_labels.import_file_label')"
        prepend-icon="mdi-file-delimited"
        show-size
      ></v-file-input>

      <div v-if="parseError" id="import_labels_error" class="import-error">
        {{ parseError }}
      </div>

      <div v-if="csvLabels.length > 0" id="import_labels_summary" class="import-summary">
        <div>
          {{ $t('set_labels.import_csv_count', { n: csvLabels.length }) }}
        </div>
        <div id="import_labels_existing">
          {{ $t('set_labels.import_existing_count', { n: existing.length }) }}
        </div>
        <div id="import_labels_missing">
          {{ $t('set_labels.import_missing_count', { n: missing.length }) }}
        </div>
      </div>

      <div v-if="missing.length > 0" id="import_labels_missing_list" class="missing-block">
        <h4>{{ $t('set_labels.import_missing_title') }}</h4>
        <v-chip
          v-for="row in displayedMissing"
          :key="row.name"
          class="ma-1"
          small
          :style="missingChipStyle(row)"
        >
          {{ row.name }}
        </v-chip>
        <v-chip v-if="hiddenMissingCount > 0" small class="ma-1">
          {{ $t('set_label_sets.selected_labels_more', { n: hiddenMissingCount }) }}
        </v-chip>

        <v-checkbox
          id="import_labels_add_missing"
          v-model="addMissing"
          :label="$t('set_labels.import_add_missing')"
          density="compact"
          hide-details
          class="mt-3"
        ></v-checkbox>
      </div>

      <v-alert
        v-if="csvLabels.length > 0 && missing.length === 0"
        id="import_labels_all_exist"
        type="info"
        variant="tonal"
        class="mt-3"
      >
        {{ $t('set_labels.import_all_exist') }}
      </v-alert>
    </template>

    <template v-slot:buttons>
      <v-btn id="import_labels_cancel" color="primary" text @click="close_modal">
        {{ $t('set_labels.modal_cancel') }}
      </v-btn>
      <v-btn
        id="import_labels_confirm"
        color="primary"
        text
        :disabled="!canImport"
        @click="confirmImport"
      >
        {{ $t('set_labels.import_confirm') }}
      </v-btn>
    </template>
  </modal>
</template>

<script>
import Modal from '@/components/Modal';
import {
  parseLabelCsv,
  partitionLabelsViaApi,
  createMissingLabels,
  applyImportDefaults,
} from '@/utils/labelCsv';

const PREVIEW_LIMIT = 20;

export default {
  name: 'ImportLabelsModal',
  components: { Modal },
  data() {
    return {
      loading: false,
      file: null,
      csvLabels: [],
      existing: [],
      missing: [],
      addMissing: true,
      parseError: null,
    };
  },
  computed: {
    displayedMissing() {
      return this.missing.slice(0, PREVIEW_LIMIT);
    },
    hiddenMissingCount() {
      return Math.max(0, this.missing.length - PREVIEW_LIMIT);
    },
    canImport() {
      if (this.csvLabels.length === 0) {
        return false;
      }
      if (this.missing.length === 0) {
        return false;
      }
      return this.addMissing;
    },
  },
  watch: {
    file() {
      this.onFileChange();
    },
  },
  methods: {
    close_modal() {
      this.$emit('close_modal');
    },
    missingChipStyle(row) {
      if (!row || !row.color) {
        return {};
      }
      return {
        backgroundColor: `rgb(${row.color})`,
        color: '#fff',
      };
    },
    async onFileChange() {
      this.parseError = null;
      this.csvLabels = [];
      this.existing = [];
      this.missing = [];
      const f = Array.isArray(this.file) ? this.file[0] : this.file;
      if (!f) {
        return;
      }
      this.loading = true;
      try {
        const text = await f.text();
        const parsed = parseLabelCsv(text);
        if (!parsed.length) {
          this.parseError = this.$t('set_labels.import_empty');
          return;
        }
        const parts = await partitionLabelsViaApi(this.$api.label, parsed);
        this.existing = parts.existing;
        const existingForColors = parts.existing.map((e) => e.box21_label || e);
        this.missing = applyImportDefaults(parts.missing, existingForColors);
        this.csvLabels = [...this.existing, ...this.missing];
        this.addMissing = this.missing.length > 0;
      } catch (e) {
        console.error(e);
        this.parseError = this.$t('set_labels.import_parse_error');
      } finally {
        this.loading = false;
      }
    },
    async confirmImport() {
      if (!this.canImport) {
        return;
      }
      this.loading = true;
      try {
        const existingForParents = this.existing.map((e) => e.box21_label || e);
        const { created } = await createMissingLabels(
          this.$api.label,
          this.missing,
          existingForParents,
        );
        this.$store.commit('label/SET_LOADED', false);
        this.$store.commit('label/SET_LOADED_FOR_PROJECT_ID', null);
        this.$store.commit('label/set_items', []);
        await this.$store.dispatch('label/refresh_total_count');
        await this.$store.dispatch('snackbars/add_success_snack', {
          message: this.$t('set_labels.import_success', { n: created.length }),
        });
        this.$emit('imported', { created });
        this.$emit('close_modal');
      } catch (e) {
        console.error(e);
        await this.$store.dispatch('snackbars/add_failed_snack', {
          message: this.$t('set_labels.import_failed'),
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.import-help {
  margin-bottom: 4px;
}
.import-format {
  margin-bottom: 4px;
  color: #555;
}
.import-defaults {
  margin-bottom: 16px;
  color: #666;
  font-size: 0.9em;
}
.import-error {
  color: #b71c1c;
  margin-bottom: 12px;
}
.import-summary {
  margin: 12px 0;
  font-weight: 500;
}
.missing-block {
  margin-top: 12px;
  padding: 12px;
  background: #fff8e1;
  border-radius: 4px;
}
</style>
